/**
 * THE TOKENIZED-ASSET PORTFOLIO (FR-U-TOK-003).
 *
 * The requirement this exists to satisfy is one sentence long and easy to miss:
 *
 *     "The token should not simply appear as an unrelated cryptocurrency balance."
 *
 * A tokenized building shown as `GVA-RE 10,000` beside a bitcoin balance has lost the only thing that
 * makes it what it is. So every row here carries the chain the whole platform is built to preserve:
 *
 *     token  ->  tokenized asset  ->  underlying asset  ->  the entity that holds it
 *
 * and it carries it by REFERENCE. The description, the location and the holding entity are joined
 * from `tokenization.asset` and `tokenization.legal_entity` on every read, never copied onto the
 * token, so a corrected address is corrected everywhere at once.
 *
 * ## Held, listed, available
 *
 * Three numbers, and they are three because conflating them is how somebody sells more than they
 * have. A holder who has listed 2,000 of 10,000 still HOLDS 10,000; what they may list again is
 * 8,000. Showing the available figure as the holding would tell them they had sold something they
 * have only offered.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader, SectionTitle } from '@/components/dash';
import { TierGate } from '@/components/gate';
import { Reveal } from '@/components/motion';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';
import { StatusPill } from '@/components/status';
import { api, LipaApiError, type SecondaryListing, type TokenHolding } from '@/lib/api';
import { formatMinor, formatUnits } from '@/lib/tokenization';

export default function TokenizePortfolio() {
  return (
    <TierGate requires="T2" what="Holding tokenized assets">
      <Portfolio />
    </TierGate>
  );
}

function Portfolio() {
  const [holdings, setHoldings] = useState<TokenHolding[] | null>(null);
  const [listings, setListings] = useState<SecondaryListing[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [h, l] = await Promise.all([
        api.tokenization.portfolio(),
        api.tokenization.myListings(),
      ]);
      setHoldings(h);
      setListings(l);
      setError(null);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load your portfolio.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function cancel(listingId: string) {
    try {
      await api.tokenization.cancelListing(listingId);
      await load();
    } catch (e) {
      setError(
        e instanceof LipaApiError ? e.detail.message : 'We could not withdraw that listing.',
      );
    }
  }

  if (error && !holdings) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!holdings) return <LoadingCard lines={5} />;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader
        title="Tokenized assets"
        subtitle="What you hold, and what each token is an interest in."
      />

      {error ? (
        <p className="rounded-field bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p>
      ) : null}

      {holdings.length === 0 ? (
        <EmptyState
          title="You hold no tokenized assets yet"
          body="Submit property, land, a vehicle or a receivable. Once it has been valued and you have agreed to the terms, your tokens appear here."
          action={{ label: 'Submit an asset', href: '/tokenize/new' }}
        />
      ) : (
        <ul className="space-y-3">
          {holdings.map((h) => (
            <li key={h.token_id}>
              <Reveal>
                <HoldingCard holding={h} />
              </Reveal>
            </li>
          ))}
        </ul>
      )}

      {listings.length > 0 ? (
        <section>
          <SectionTitle>Your listings</SectionTitle>
          <ul className="space-y-2.5">
            {listings.map((l) => (
              <li
                key={l.listing_id}
                className="rounded-card border border-border bg-surface p-4 shadow-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">
                      {l.underlying.description ?? l.token_name ?? l.symbol}
                    </p>
                    <p className="text-micro text-ink-subtle">
                      {formatUnits(l.units_available)} of {formatUnits(l.units_listed)} still
                      available &middot; {l.quote_asset} {formatMinor(l.price, l.quote_asset)} per
                      token
                    </p>
                  </div>
                  <button
                    onClick={() => void cancel(l.listing_id)}
                    className="shrink-0 rounded-field border border-border px-2.5 py-1.5 text-micro font-medium text-ink transition hover:bg-surface-sunken"
                  >
                    Withdraw
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function HoldingCard({ holding: h }: { holding: TokenHolding }) {
  const pledged = h.state === 'locked';

  return (
    <article className="rounded-card border border-border bg-surface p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">
            {h.underlying.description ?? h.name ?? 'Tokenized asset'}
          </p>
          <p className="truncate text-micro text-ink-subtle">
            {h.symbol}
            {h.underlying.location ? ` · ${h.underlying.location}` : ''}
          </p>
        </div>
        <StatusPill
          status={pledged ? 'locked' : 'verified'}
          label={pledged ? 'Pledged' : 'Active'}
        />
      </div>

      {/* HELD, LISTED, AVAILABLE. Three numbers, because conflating them is how somebody sells more
          than they have. */}
      <div className="mt-3 grid grid-cols-3 gap-3 border-t border-border pt-3">
        <Mini label="Tokens held" value={formatUnits(h.units)} />
        <Mini label="Listed" value={formatUnits(h.units_listed)} />
        <Mini label="Available" value={formatUnits(h.units_available)} accent />
      </div>

      {h.value ? (
        <p className="mt-2 text-micro text-ink-subtle">
          Worth {h.quote_asset ?? 'KES'} {formatMinor(h.value, h.quote_asset ?? 'KES')} at the issue
          price of {formatMinor(h.unit_value, h.quote_asset ?? 'KES')} per token.
        </p>
      ) : null}

      {/* THE RELATIONSHIP, spelled out. This is the difference between a token and a balance. */}
      <dl className="mt-3 space-y-1 rounded-field bg-surface-sunken px-3 py-2.5">
        <Line
          k="Underlying asset"
          v={h.underlying.description ?? h.underlying.ownership_reference}
        />
        <Line k="Reference" v={h.underlying.ownership_reference} mono />
        {h.underlying.held_by ? <Line k="Held by" v={h.underlying.held_by} /> : null}
        {h.underlying.product ? <Line k="Product" v={h.underlying.product} /> : null}
      </dl>

      <div className="mt-3 flex gap-2.5">
        {h.underlying.application_id ? (
          <Link
            to={`/tokenize/applications/${h.underlying.application_id}`}
            className="flex-1 rounded-field border border-border py-2.5 text-center text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            The record
          </Link>
        ) : null}
        {pledged ? (
          <span className="flex-1 rounded-field bg-surface-sunken py-2.5 text-center text-sm text-ink-subtle">
            Pledged as collateral
          </span>
        ) : (
          <Link
            to={`/tokenize/tokens/${h.token_id}/list`}
            className="flex-1 rounded-field bg-primary py-2.5 text-center text-sm font-medium text-white transition hover:bg-primary-light"
          >
            List some for sale
          </Link>
        )}
      </div>
    </article>
  );
}

function Mini({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <p className="text-nano text-ink-subtle">{label}</p>
      <p className={`tabular truncate text-sm font-medium ${accent ? 'text-brand' : 'text-ink'}`}>
        {value}
      </p>
    </div>
  );
}

function Line({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-nano text-ink-subtle">{k}</dt>
      <dd className={`truncate text-micro text-ink ${mono ? 'font-mono' : ''}`}>{v}</dd>
    </div>
  );
}
